/**
 * Key-value pair for structured error properties.
 * Mirrors the format used by `throwUserInputError` and `ApolloErrorHandlerService`.
 */
export interface IProgressErrorProperty {
  key: string;
  value: string;
}

/**
 * Single entry within an aggregate error.
 * Each entry corresponds to one inner `UserInputError` from a `throwAggregateUserInputError` call.
 */
export interface IProgressAggregateErrorEntry {
  errorMessageKey: string;
  errorProperties?: IProgressErrorProperty[] | null;
}

/**
 * Shared interface for the GraphQL ProgressEvent type.
 *
 * Implemented by:
 * - Backend `ProgressEvent` class (NestJS `@ObjectType`)
 * - Frontend generated `ProgressEvent` class (codegen output)
 *
 * Used by:
 * - `reportProgress` operator (frontendShared) for type-safe progress filtering
 * - `ProgressNotificationService` for progress UI updates
 * - `RequestContextService` for publishing progress/error events
 *
 * Fields are nullable to match the GraphQL schema (`nullable: true`).
 */
export interface IProgressEvent {
  progress?: number | null;
  progressMessage?: string | null;
  /** Error code (e.g. `"BAD_USER_INPUT"`, `"AGGREGATEERROR"`). Present only on error events. */
  errorCode?: string | null;
  /** Translation key for the error (e.g. `"PROCESSNAMENOTUNIQUE"`). Present only on structured errors. */
  errorMessageKey?: string | null;
  /** Additional properties for error message interpolation (same format as `throwUserInputError` props). */
  errorProperties?: IProgressErrorProperty[] | null;
  /** Individual errors from an `AggregateError`. Present only when `errorCode === "AGGREGATEERROR"`. */
  aggregateErrors?: IProgressAggregateErrorEntry[] | null;
  /** Identifies a sub-progress lane (e.g. one process being activated); `null`/`undefined` = total progress. */
  subKey?: string | null;
  /** Human-readable label of the sub-progress lane (e.g. the process display name). */
  subLabel?: string | null;
  /** `true` when the sub-progress lane has finished and should be removed from the UI. */
  subDone?: boolean | null;
}

/** The GraphQL `__typename` value for ProgressEvent objects. */
export const PROGRESS_EVENT_TYPENAME = "ProgressEvent";

/** Returns `true` when the event signals an error (negative progress). */
export function isProgressError(event: IProgressEvent): boolean {
  return event.progress != null && event.progress < 0;
}

/** Returns `true` when the error event carries structured error info (translation key). */
export function isStructuredProgressError(event: IProgressEvent): boolean {
  return isProgressError(event) && !!event.errorMessageKey;
}

/** Returns `true` when the error event carries aggregate errors (multiple validation errors). */
export function isAggregateProgressError(event: IProgressEvent): boolean {
  return isProgressError(event) && !!event.aggregateErrors?.length;
}

/** Extracts the user-facing error message from an error progress event. */
export function getProgressErrorMessage(event: IProgressEvent): string {
  return event.progressMessage?.replace(/^Error:\s*/i, "") || "Unknown error";
}
